import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import { Flex, NativeSelect, Text } from "@chakra-ui/react";
import { defineMessages, useIntl } from "react-intl";
import { RangeMetric } from "./MetricFilter";
import { system } from "@/styles/theme";
import { METRIC_CONFIG } from "@/utils/metricUtils";
const optionStyle = {
  backgroundColor: system.token("colors.white"),
  color: system.token("colors.text")
};
export interface RangeFilterInputProps {
  metric: MetricWithSelectableRange;
  datasetMin: number;
  datasetMax: number;
  currentMin: number;
  currentMax: number;
  onRangeChange: (range: {
    min: number;
    max: number;
  }) => void;
}
type MetricWithSelectableRange = Extract<RangeMetric, "speed" | "acceleration">;
const messages = defineMessages({
  showFrom: {
    id: "rangeFilter.showFrom",
    defaultMessage: "Show {metric} from"
  },
  to: {
    id: "rangeFilter.to",
    defaultMessage: "to"
  },
  error: {
    id: "rangeFilter.error",
    defaultMessage: "Minimum must not equal or exceed maximum"
  },
  speed: {
    id: "rangeFilter.showSpeed",
    defaultMessage: "speed"
  },
  acceleration: {
    id: "rangeFilter.showAcceleration",
    defaultMessage: "acceleration"
  }
});
const PresetSelect = t0 => {
  const $ = _c(12);
  const {
    value,
    onChange,
    disabled,
    presets,
    hasError,
    testId
  } = t0;
  const t1 = disabled ? "" : String(value);
  const t2 = hasError ? "red" : "grey.200";
  let t3;
  if ($[0] !== disabled || $[1] !== presets) {
    t3 = disabled ? <option value="">...</option> : presets.map(_temp);
    $[0] = disabled;
    $[1] = presets;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== onChange || $[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = <NativeSelect.Field value={t1} onChange={onChange} bg="white" color="black" borderColor={t2} minWidth="70px">{t3}</NativeSelect.Field>;
    $[3] = onChange;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <NativeSelect.Indicator />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== testId) {
    t6 = <NativeSelect.Root size="sm" width="auto" data-testid={testId}>{t4}{t5}</NativeSelect.Root>;
    $[9] = t4;
    $[10] = testId;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};
export function generatePresets(min: number, max: number, step: number): number[] {
  if (step <= 0) return [];
  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;
  const presets: number[] = [];
  for (let v = start; v <= end; v += step) {
    presets.push(v);
  }
  return presets;
}
export const RangeFilterInput = t0 => {
  const $ = _c(66);
  const {
    metric,
    datasetMin,
    datasetMax,
    currentMin,
    currentMax,
    onRangeChange
  } = t0;
  const intl = useIntl();
  const {
    unit,
    step
  } = METRIC_CONFIG[metric];
  let t1;
  if ($[0] !== intl || $[1] !== unit) {
    t1 = intl.formatMessage(unit);
    $[0] = intl;
    $[1] = unit;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const unitLabel = t1;
  const metricLabel = intl.formatMessage(messages[metric]);
  const loaded = datasetMin < datasetMax;
  let t2;
  if ($[3] !== datasetMax || $[4] !== datasetMin || $[5] !== loaded || $[6] !== step) {
    t2 = loaded ? generatePresets(datasetMin, datasetMax, step) : [];
    $[3] = datasetMax;
    $[4] = datasetMin;
    $[5] = loaded;
    $[6] = step;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const presets = t2;
  let t3;
  if ($[8] !== currentMin || $[9] !== step) {
    t3 = () => Math.floor(currentMin / step) * step;
    $[8] = currentMin;
    $[9] = step;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const [localMin, setLocalMin] = useState(t3);
  let t4;
  if ($[11] !== currentMax || $[12] !== step) {
    t4 = () => Math.ceil(currentMax / step) * step;
    $[11] = currentMax;
    $[12] = step;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const [localMax, setLocalMax] = useState(t4);
  let t5;
  let t6;
  if ($[14] !== currentMin || $[15] !== step) {
    t5 = () => setLocalMin(Math.floor(currentMin / step) * step);
    t6 = [currentMin, step];
    $[14] = currentMin;
    $[15] = step;
    $[16] = t5;
    $[17] = t6;
  } else {
    t5 = $[16];
    t6 = $[17];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[18] !== currentMax || $[19] !== step) {
    t7 = () => setLocalMax(Math.ceil(currentMax / step) * step);
    t8 = [currentMax, step];
    $[18] = currentMax;
    $[19] = step;
    $[20] = t7;
    $[21] = t8;
  } else {
    t7 = $[20];
    t8 = $[21];
  }
  useEffect(t7, t8);
  const hasError = localMin >= localMax;
  let t9;
  if ($[22] !== localMax || $[23] !== onRangeChange) {
    t9 = e => {
      const min = Number(e.target.value);
      setLocalMin(min);
      if (min < localMax) {
        onRangeChange({
          min,
          max: localMax
        });
      }
    };
    $[22] = localMax;
    $[23] = onRangeChange;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const handleMinChange = t9;
  let t10;
  if ($[25] !== localMin || $[26] !== onRangeChange) {
    t10 = e_0 => {
      const max = Number(e_0.target.value);
      setLocalMax(max);
      if (localMin < max) {
        onRangeChange({
          min: localMin,
          max
        });
      }
    };
    $[25] = localMin;
    $[26] = onRangeChange;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const handleMaxChange = t10;
  const t11 = !loaded;
  let t12;
  if ($[28] !== hasError || $[29] !== presets || $[30] !== t11) {
    t12 = {
      disabled: t11,
      presets,
      hasError
    };
    $[28] = hasError;
    $[29] = presets;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const selectProps = t12;
  const T0 = Flex;
  const t13 = "column";
  const t14 = "1";
  const T1 = Flex;
  const t15 = "center";
  const t16 = "2";
  const t17 = "wrap";
  const T2 = Text;
  const t18 = "sm";
  const t19 = "textMuted";
  const t20 = intl.formatMessage(messages.showFrom, {
    metric: metricLabel
  });
  let t21;
  if ($[32] !== T2 || $[33] !== t20) {
    t21 = <T2 fontSize={t18} color={t19}>{t20}</T2>;
    $[32] = T2;
    $[33] = t20;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== handleMinChange || $[36] !== localMin || $[37] !== selectProps) {
    t22 = <PresetSelect {...selectProps} value={localMin} onChange={handleMinChange} testId="range-min-input" />;
    $[35] = handleMinChange;
    $[36] = localMin;
    $[37] = selectProps;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== unitLabel) {
    t23 = <Text fontSize="sm" color="textMuted">{unitLabel}</Text>;
    $[39] = unitLabel;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== intl) {
    t24 = intl.formatMessage(messages.to);
    $[41] = intl;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== t24) {
    t25 = <Text fontSize="sm" color="textMuted">{t24}</Text>;
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== handleMaxChange || $[46] !== localMax || $[47] !== selectProps) {
    t26 = <PresetSelect {...selectProps} value={localMax} onChange={handleMaxChange} testId="range-max-input" />;
    $[45] = handleMaxChange;
    $[46] = localMax;
    $[47] = selectProps;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== unitLabel) {
    t27 = <Text fontSize="sm" color="textMuted">{unitLabel}</Text>;
    $[49] = unitLabel;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] !== T1 || $[52] !== t21 || $[53] !== t22 || $[54] !== t23 || $[55] !== t25 || $[56] !== t26 || $[57] !== t27) {
    t28 = <T1 align={t15} gap={t16} wrap={t17}>{t21}{t22}{t23}{t25}{t26}{t27}</T1>;
    $[51] = T1;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
    $[55] = t25;
    $[56] = t26;
    $[57] = t27;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== hasError || $[60] !== intl) {
    t29 = hasError && <Text data-testid="range-error" color="red" fontSize="xs">{intl.formatMessage(messages.error)}</Text>;
    $[59] = hasError;
    $[60] = intl;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== T0 || $[63] !== t28 || $[64] !== t29) {
    t30 = <T0 direction={t13} gap={t14}>{t28}{t29}</T0>;
    $[62] = T0;
    $[63] = t28;
    $[64] = t29;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  return t30;
};
function _temp(p) {
  return <option key={p} value={String(p)} style={optionStyle}>{p}</option>;
}